var Control             = require("enyo/Control"),
    EventEmitterSupport = require("enyo-ui/EventEmitterSupport"),
    FadeSupport         = require("enyo-ui/FadeSupport"),
    FloatingSupport     = require("enyo-ui/FloatingSupport"),
    Popup,
    Scrim               = require("enyo-ui/Scrim"),
    kind                = require("enyo/kind"),
    utils               = require("enyo/utils");

require("enyo-ui");

/**
 * Fires immediately before the Popup starts revealing itself.
 *
 * @event     enyo-ui.Popup#revealing
 * @type      {object}
 * @property  {enyo.Control}  sender  The popup that emitted the event
 */

/**
 * Fires when the Popup is absoluteShowing, not fading and fully faded in.
 *
 * @event     enyo-ui.Popup#revealed
 * @type      {object}
 * @property  {enyo.Control}  sender  The popup that emitted the event
 */

/**
 * Fires immediately before the Popup starts hiding itself.
 *
 * @event     enyo-ui.Popup#hiding
 * @type      {object}
 * @property  {enyo.Control}  sender  The popup that emitted the event
 */

/**
 * Fires when the Popup is not absoluteShowing, not fading and fully faded out.
 *
 * @event     enyo-ui.Popup#hidden
 * @type      {object}
 * @property  {enyo.Control}  sender  The popup that emitted the event
 */

/**
 * [Popup]{@link enyo-ui.Popup} aims to be a simple popup kind with with a
 * fade-in and fade-out animation, floating support and optional use of a scrim.
 *
 * It is deliberately kept much simpler than enyo.Popup to make it easier to
 * extend for specific layout or interface needs.
 *
 * @ui
 * @class   enyo-ui.Popup
 * @extends enyo.Popup
 * @mixes   enyo-ui.EventEmitterSupport
 * @mixes   enyo-ui.FadeSupport
 * @mixes   enyo-ui.FloatingSupport
 * @public
 */
Popup = module.exports = kind(/** @lends  enyo-ui.Popup.prototype */ {

    /**
     * @private
     */
    name: "enyo-ui.Popup",

    /**
     * @private
     */
    kind: Control,

    /**
     * @private
     */
    mixins: [
        EventEmitterSupport,
        FadeSupport,
        FloatingSupport
    ],

    /**
     * @private
     */
    classes: "enyo-ui-popup",

    /**
     * @type {String}
     * @default dialog
     * @public
     */
    accessibilityRole: "dialog",

    /**
     * @public
     */
    showing: false,

    /**
     * @public
     */
    initialOpacity: 0,

    /**
     * The scrim instance will be stored here.
     *
     * @type {enyo.Control}
     * @public
     */
    scrim: null,

    /**
     * Boolean that controls whether or not a scrim will be displayed.
     *
     * @type    {boolean}
     * @default false
     * @public
     */
    useScrim: false,

    /**
     * The kind to use as scrim.
     *
     * @type    {enyo.Control}
     * @default enyo-ui.Scrim
     * @public
     */
    scrimKind: Scrim,

    /**
     * Options for the scrim.
     *
     * @type {?object}
     * @public
     */
    scrimOptions: {},

    /**
     * @private
     */
    handlers: {
        onRequestShow: "onRequestShow",
        onRequestHide: "onRequestHide"
    },

    /**
     * @private
     */
    observers: {
        emitShowingEvents: ["absoluteShowing", "fading"]
    },

    /**
     * Builds the scrim control.
     *
     * @returns {enyo.Control} The scrim
     * @private
     */
    createScrim: function() {
        var scrimKind    = this.getScrimKind(),
            scrimOptions = this.getScrimOptions(scrimKind),
            scrim;

        scrim = this.scrim = this.createComponent({
            name:      "scrim",
            kind:      scrimKind,
            classes:   "enyo-ui-popup__scrim " + (scrimOptions.classes || ""),
            isChrome:  true,
            addBefore: this,
            floating:  this.floating
        }, scrimOptions);

        // Make sure scrim renders into parent, before popup
        !scrim.floating && scrim.setParent(this.getParent());

        return this.scrim;
    },

    /**
     * Override this method if the returned kind needs to be computed.
     *
     * @return {Function}  The kind constructor
     * @protected
     */
    getScrimKind: function() {
        return this.scrimKind;
    },

    /**
     * Override this method if the returned options need to change based on the
     * kind that will be used.
     *
     * @param  {Function}  kind The scrimKind
     * @return {?Object}   The options
     * @protected
     */
    getScrimOptions: function() {
        return this.scrimOptions || {};
    },

    /**
     * Emits `revealed` event when popup is absoluteShowing and no longer fading.
     * Emits `hidden` event when popup is not absoluteShowing and no longer
     * fading.
     *
     * @fires  enyo-ui.Popup#revealed
     * @fires  enyo-ui.Popup#hidden
     * @param  {*}  was  The previous value
     * @param  {*}  is   The current value
     * @private
     */
    emitShowingEvents: function(was, is) {
        if (was === is || this.fading) {
            return;
        }

        if (this.absoluteShowing && this.isFadedIn()) {
            this.emit("revealed", {
                sender: this
            });
        }
        else if (!this.absoluteShowing && this.isFadedOut()) {
            this.emit("hidden", {
                sender: this
            });
        }
    },

    /**
     * @param  {Control}  sender  The sender of the event
     * @param  {Object}   event   The event object, which may contain fade and
     *                            snap settings
     * @return {boolean}  Prevent propagation
     * @private
     */
    onRequestShow: function(sender, event) {
        var fade = typeof event.fade === "boolean" ? event.fade : true,
            snap = typeof event.snap === "boolean" ? event.snap : false;

        this.requestShow(fade, snap);

        return true;
    },

    /**
     * @param  {Control}  sender  The sender of the event
     * @param  {Object}   event   The event object, which may contain fade and
     *                            snap settings
     * @return {boolean}  Prevent propagation
     * @private
     */
    onRequestHide: function(sender, event) {
        var fade = typeof event.fade === "boolean" ? event.fade : true,
            snap = typeof event.snap === "boolean" ? event.snap : false;

        this.requestHide(fade, snap);

        return true;
    },

    /**
     * Before starting the transition to reveal the popup, we'll make sure it's
     * rendered and displayed in the DOM, even if still visually hidden.
     *
     * @private
     */
    prepareForReveal: function() {
        // If not rendered, first make sure popup is `absoluteShowing` yet
        // visually hidden, then render. This prevents the popup's content from
        // deferring render-related tasks due to not being `absoluteShowing`.
        if (!this.hasNode()) {
            this.fadeTo(this.initialOpacity, true);
            this.show();
            this.render();
        }
        else if (!this.get("showing")) {
            this.fadeTo(0, true);
            this.show();
        }
    },

    /**
     * Reveals the popup by rendering it if needed and then fading in.
     *
     * @fires  enyo-ui.Popup#revealing
     * @param  {boolean=}  fade  `true` if popup and scrim should fade, `false`
     *                            otherwise
     * @param  {boolean=}  snap  `true` if fade should be instant, `false`
     *                            otherwise
     * @return {boolean}   Stop propagation.
     * @public
     */
    requestShow: function(fade, snap) {
        this.prepareForReveal();

        // Now that the popup has a node, we'll send an event that it's
        // revealing. We wait until after the popup has a node so
        // event-listeners can inspect it's dimensions if needed.
        this.emit("revealing", {
            sender: this
        });

        // Now that we know the popup is rendered, fade it in.
        this.useScrim && this.showScrim(fade, snap);
        this.show(fade, snap);

        return true;
    },

    /**
     * Hides the popup by fading it out if needed.
     *
     * @fires  enyo-ui.Popup#hiding
     * @param  {boolean=}  fade  `true` if popup and scrim should fade, `false`
     *                           otherwise
     * @param  {boolean=}  snap  `true` if fade should be instant, `false`
     *                           otherwise
     * @return {boolean}   Stop propagation.
     * @public
     */
    requestHide: function(fade, snap) {
        this.emit("hiding", {
            sender: this
        });
        this.hideScrim(fade, snap);
        this.hide(fade, snap);

        return true;
    },

    /**
     * Create a scrim if needed and fade it in.
     *
     * @param  {boolean=}  fade  `true` if scrim should fade, `false`
     *                            otherwise
     * @param  {boolean=}  snap  `true` if fade should be instant, `false`
     *                            otherwise
     * @private
     */
    showScrim: function(fade, snap) {
        var scrim = this.scrim;

        if (!scrim) {
            scrim = this.createScrim();
        }

        !scrim.hasNode() && scrim.render();
        scrim.requestShow(fade, snap);
    },

    /**
     * Check for a scrim and if found, fade it out.
     *
     * @param  {boolean=}  fade  `true` if scrim should fade, `false`
     *                           otherwise
     * @param  {boolean=}  snap  `true` if fade should be instant, `false`
     *                           otherwise
     * @private
     */
    hideScrim: function(fade, snap) {
        var scrim = this.scrim;

        scrim && scrim.requestHide(fade, snap);
    },

    /**
     * Closes the popup. By default this simply hides the popup. Override if the
     * popup also needs to be destroyed.
     *
     * @public
     */
    close: function() {
        this.requestHide(true, false);
    }

});

/**
 * This method allows enyo.kind to extend the `scrimOptions` hash when creating
 * super kinds.
 *
 * @param    {Function}  ctor   The constructor
 * @param    {Object}    props  The properties
 * @memberof Repeater
 * @static
 * @private
 */
Popup.concat = function(ctor, props) {
    var proto = ctor.prototype || ctor,
        scrimOptions;

    if (props.scrimOptions) {
        scrimOptions       = proto.scrimOptions ? utils.clone(proto.scrimOptions) : {};
        proto.scrimOptions = utils.mixin(scrimOptions, props.scrimOptions);
        delete props.scrimOptions;
    }
};
